import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { addDaysLocal } from "@/lib/bookingDates";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import type { PlannerCustomEntry } from "@/types/camping";

export interface PlannerCustomEntryModalProps {
  open: boolean;
  entry: PlannerCustomEntry | null;
  defaultRangeFrom?: string;
  defaultRangeTo?: string;
  presetLabel?: string;
  lockLabel?: boolean;
  /** Letzter Tag inklusive. Gespeichert wird der Folgetag, wie bei Buchungen. */
  endInclusive?: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const COLOR_OPTIONS = [
  { value: "", label: "Standard (Blau)" },
  { value: "#6366f1", label: "Indigo" },
  { value: "#8b5cf6", label: "Violett" },
  { value: "#ec4899", label: "Pink" },
  { value: "#f59e0b", label: "Orange" },
  { value: "#10b981", label: "Grün" },
  { value: "#64748b", label: "Grau" },
];

export function PlannerCustomEntryModal({
  open,
  entry,
  defaultRangeFrom,
  defaultRangeTo,
  presetLabel,
  lockLabel = false,
  endInclusive = false,
  onClose,
  onSaved,
}: PlannerCustomEntryModalProps) {
  const editing = Boolean(entry?.id);
  const quickCreate = !editing && lockLabel;
  const [label, setLabel] = useState("");
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [color, setColor] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLabel(entry?.label ?? presetLabel ?? "");
    setTitle(entry?.title ?? "");
    setStartDate(entry?.start_date ?? defaultRangeFrom ?? "");
    const storedEnd = entry?.end_date ?? defaultRangeTo ?? "";
    setEndDate(endInclusive && entry?.end_date ? addDaysLocal(entry.end_date, -1) : storedEnd);
    setColor(entry?.color ?? "");
    setNotes(entry?.notes ?? "");
    setError(null);
  }, [open, entry, presetLabel, defaultRangeFrom, defaultRangeTo, endInclusive]);

  async function handleSave() {
    if (!label.trim() || !title.trim() || !startDate || !endDate) {
      setError("Bitte Zeilenname, Titel und Zeitraum ausfüllen.");
      return;
    }
    if (endInclusive ? endDate < startDate : endDate <= startDate) {
      setError(
        endInclusive
          ? "Das Enddatum muss am oder nach dem Startdatum liegen."
          : "Das Enddatum muss nach dem Startdatum liegen.",
      );
      return;
    }
    const storedEnd = endInclusive ? addDaysLocal(endDate, 1) : endDate;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        label: label.trim(),
        title: title.trim(),
        start_date: startDate,
        end_date: storedEnd,
        color: color || null,
        notes: notes.trim() || null,
      };
      if (editing && entry) {
        await api.patch<PlannerCustomEntry>(
          `/api/v1/planner/custom-entries/${entry.id}`,
          payload,
        );
      } else {
        await api.post<PlannerCustomEntry>("/api/v1/planner/custom-entries", payload);
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!entry?.id) return;
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    setDeleting(true);
    setError(null);
    try {
      await api.del(`/api/v1/planner/custom-entries/${entry.id}`);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Löschen fehlgeschlagen.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        endInclusive
          ? editing
            ? "Veranstaltung bearbeiten"
            : "Veranstaltung hinzufügen"
          : editing
            ? "Eintrag bearbeiten"
            : quickCreate
              ? "Eintrag hinzufügen"
              : "Eigene Zeile hinzufügen"
      }
      description={
        endInclusive
          ? "Der Termin erscheint im Planer und unter dem Zeitraum betroffener Buchungen."
          : quickCreate
            ? "Neuer Belegungsbalken in der gewählten Zeile."
            : "Veranstaltungen, Blocker oder andere Planungszeilen ohne Stellplatzbezug."
      }
      size="md"
      footer={
        <div className="flex w-full flex-wrap items-center gap-2">
          {editing ? (
            <Button type="button" variant="danger" loading={deleting} onClick={() => void handleDelete()}>
              Löschen
            </Button>
          ) : null}
          <div className="ml-auto flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="button" loading={saving} onClick={() => void handleSave()}>
              Speichern
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {endInclusive ? null : (
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-label">Zeilenname</span>
          <input
            className="eh-input w-full"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="z. B. Veranstaltungen, Werkstatt"
            readOnly={lockLabel}
            disabled={lockLabel}
          />
        </label>
        )}
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-label">Titel im Balken</span>
          <input
            className="eh-input w-full"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="z. B. Sommerfest, Dachsanierung"
          />
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-label">Von (inkl.)</span>
            <input
              type="date"
              className="eh-input w-full"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-label">
              {endInclusive ? "Bis (inkl.)" : "Bis (exkl., Abreisetag)"}
            </span>
            <input
              type="date"
              className="eh-input w-full"
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
        </div>
        {!quickCreate || endInclusive ? (
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-label">Farbe</span>
            <select className="eh-input w-full" value={color} onChange={(e) => setColor(e.target.value)}>
              {COLOR_OPTIONS.map((option) => (
                <option key={option.value || "default"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-label">Notizen (optional)</span>
          <textarea
            className="eh-input w-full min-h-[4rem]"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
      </div>
    </Modal>
  );
}
